<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :width="width"
    :persistent="persistent"
    :eager="eager"
  >
    <template #activator="{ props: activatorProps }">
      <slot name="activator" :props="activatorProps"></slot>
    </template>

    <v-card class="oare-dialog-card">
      <div v-if="closeButton || showActionsBar || actionTitle">
        <v-card-actions>
          <slot name="pre-actions" />
          <h2 class="ml-3" v-if="actionTitle">{{ actionTitle }}</h2>
          <v-spacer />
          <slot name="action-options" />
          <v-btn
            v-if="closeButton"
            @click="$emit('update:modelValue', false)"
            variant="text"
            color="error"
            >Close</v-btn
          >
        </v-card-actions>
        <v-divider />
      </div>
      <v-card-title data-testid="dialog-title" class="test-dialog-title">
        <slot name="title">{{ title }}</slot>
      </v-card-title>
      <v-card-text>
        <slot></slot>
      </v-card-text>
      <div v-if="showCancel || showSubmit">
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="showCancel"
            @click="$emit('update:modelValue', false)"
            color="error"
            variant="text"
            :disabled="cancelDisabled"
            data-testid="cancel-btn"
            >{{ cancelText }}</v-btn
          >

          <v-btn
            v-if="showActionButton"
            @click="$emit('action')"
            color="primary"
            variant="text"
            :disabled="actionButtonDisabled"
            >{{ actionButtonText }}</v-btn
          >

          <slot
            v-if="showSubmit"
            name="submit-button"
            :props="{ onClick: submit }"
          >
            <OareLoaderButton
              class="test-submit-btn"
              @click="submit"
              color="primary"
              variant="elevated"
              :loading="submitLoading"
              :disabled="submitLoading || submitDisabled"
            >
              {{ submitText }}
            </OareLoaderButton>
          </slot>
        </v-card-actions>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'OareDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    showSubmit: {
      type: Boolean,
      default: true,
    },
    showCancel: {
      type: Boolean,
      default: true,
    },
    persistent: {
      type: Boolean,
      default: true,
    },
    submitText: {
      type: String,
      default: 'Submit',
    },
    cancelText: {
      type: String,
      default: 'Cancel',
    },
    submitLoading: {
      type: Boolean,
      default: false,
    },
    submitDisabled: {
      type: Boolean,
      default: false,
    },
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    closeOnSubmit: {
      type: Boolean,
      default: false,
    },
    width: {
      type: [Number, String],
      default: 500,
    },
    closeButton: {
      type: Boolean,
      default: false,
    },
    actionTitle: {
      type: String,
      required: false,
    },
    eager: {
      type: Boolean,
      default: false,
    },
    showActionsBar: {
      type: Boolean,
      default: false,
    },
    showActionButton: {
      type: Boolean,
      default: false,
    },
    actionButtonText: {
      type: String,
      default: 'Action',
    },
    actionButtonDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'action', 'submit'],
  setup(props, { emit }) {
    const submit = () => {
      if (props.closeOnSubmit) {
        emit('update:modelValue', false);
      }
      emit('submit');
    };

    return {
      submit,
    };
  },
});
</script>

<style scoped>
.oare-dialog-card > .v-card-title {
  font-size: 1.25rem;
  font-weight: 500;
  letter-spacing: 0.0125em;
  line-height: 2rem;
  padding: 16px 24px 10px;
}

.oare-dialog-card > .v-card-text {
  padding: 0 24px 20px;
}

.oare-dialog-card > .v-card-actions,
.oare-dialog-card > div > .v-card-actions {
  padding: 8px 16px;
}
</style>
